import { useState, useRef } from "react";
import { usePlatformSummary, type PlatformCompanySummary } from "@/hooks/usePlatformSummary";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useAdminRole } from "@/hooks/useAdminRole";
import { supabase } from "@/integrations/supabase/client";
import {
  Building2, DollarSign,
  Users, CreditCard, Search, Settings,
  TrendingUp, TrendingDown, AlertTriangle, BarChart3, UserCheck, ExternalLink,
  RefreshCw, Loader2,
} from "lucide-react";
import { useOpenIncidentCount, useTenantIncidents } from "@/hooks/useTenantIncidents";
import { IncidentCard } from "@/components/platform/incidents/IncidentCard";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import OnboardCompanyDialog from "@/components/platform/OnboardCompanyDialog";
import EditSubscriptionDialog from "@/components/platform/EditSubscriptionDialog";
import { useMrrTrend } from "@/hooks/useTenantBilling";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ResponsiveContainer, AreaChart, Area } from "recharts";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);

const KpiCard = ({ label, value, icon: Icon, sub, badge, onClick, active }: {
  label: string; value: string; icon: React.ElementType; sub?: string;
  badge?: { text: string; variant: "default" | "destructive" | "secondary" };
  onClick?: () => void; active?: boolean;
}) => (
  <Card
    className={`transition-colors ${onClick ? "cursor-pointer hover:border-primary/40" : ""} ${active ? "border-primary ring-1 ring-primary/20" : ""}`}
    onClick={onClick}
  >
    <CardContent className="flex items-center gap-4 p-4">
      <div className="rounded-lg bg-primary/10 p-3">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className="flex items-center gap-2">
          <p className="text-xl font-bold">{value}</p>
          {badge && (
            <Badge variant={badge.variant} className="text-[10px] px-1.5 py-0">
              {badge.text}
            </Badge>
          )}
        </div>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const tierColor: Record<string, string> = {
  base: "bg-primary text-primary-foreground",
  starter: "bg-secondary text-secondary-foreground",
  pro: "bg-primary text-primary-foreground",
  enterprise: "bg-accent text-accent-foreground",
};
const statusColor: Record<string, string> = {
  active: "bg-primary text-primary-foreground",
  trial: "bg-secondary text-secondary-foreground",
  past_due: "bg-destructive text-destructive-foreground",
  suspended: "bg-destructive text-destructive-foreground",
  cancelled: "bg-muted text-muted-foreground",
};

const STATUS_FILTERS = ["all", "active", "trial", "past_due", "suspended", "cancelled"] as const;

const PlatformAdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useTabNavigate();
  const { can, isLoading: adminLoading } = useAdminRole();
  const { data: companies = [], isLoading, error } = usePlatformSummary();
  const { trendData, currentMrr } = useMrrTrend(30);
  const { startImpersonation } = useImpersonation();
  const openIncidentCount = useOpenIncidentCount();
  const { data: criticalIncidents = [] } = useTenantIncidents({
    status: "open",
    severity: "all",
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [churnOnly, setChurnOnly] = useState(false);
  const [editCompany, setEditCompany] = useState<PlatformCompanySummary | null>(null);
  const [impersonateTarget, setImpersonateTarget] = useState<PlatformCompanySummary | null>(null);



  const filtered = companies.filter((c) => {
    if (statusFilter !== "all" && c.subscription_status !== statusFilter) return false;
    if (churnOnly && !(c.project_count === 0 && c.subscription_status === "active")) return false;
    if (search && !c.company_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totals = companies.reduce(
    (acc, c) => ({
      companies: acc.companies + 1,
      mrr: acc.mrr + c.calculated_mrr,
      projects: acc.projects + c.project_count,
      users: acc.users + c.active_users,
      seats: acc.seats + c.seat_count,
      active: acc.active + (c.subscription_status === "active" ? 1 : 0),
      trial: acc.trial + (c.subscription_status === "trial" ? 1 : 0),
    }),
    { companies: 0, mrr: 0, projects: 0, users: 0, seats: 0, active: 0, trial: 0 },
  );

  const churnRiskCount = companies.filter(
    (c) => c.project_count === 0 && c.subscription_status === "active"
  ).length;

  const mrrChange = trendData.length >= 2
    ? trendData[trendData.length - 1].mrr - trendData[0].mrr
    : 0;

  const payingCount = companies.filter(
    (c) => c.subscription_status === "active" && c.calculated_mrr > 0
  ).length;
  const avgRevenue = payingCount > 0 ? totals.mrr / payingCount : 0;

  const handleImpersonate = async () => {
    if (!impersonateTarget) return;
    await startImpersonation(impersonateTarget.company_id, impersonateTarget.company_name);
    setImpersonateTarget(null);
    navigate("/controller");
  };

  const handleKpiClick = (type: string) => {
    setChurnOnly(false);
    if (type === "active") { setStatusFilter("active"); }
    else if (type === "trial") { setStatusFilter("trial"); }
    else if (type === "churn") { setStatusFilter("all"); setChurnOnly(true); }
    else if (type === "mrr" || type === "arr" || type === "avg") {
      navigate("/platform/billing");
    }
  };



  return (
    <div className="space-y-6">
      <SEO title="Platform Admin" description="SaaS management console" />

      {/* Action bar */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Platform overview and company management</p>
        </div>
        <div className="flex items-center gap-2">
          {(adminLoading || can("change_billing")) && <OnboardCompanyDialog />}
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-20">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="p-6 text-destructive">
            Failed to load platform data. You may not have platform admin access.
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && (
        <>
          {/* KPI Cards */}
          <section>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard label="Total MRR" value={fmt(totals.mrr)} icon={CreditCard} onClick={() => handleKpiClick("mrr")} />
              <KpiCard
                label="MRR Change (30d)"
                value={`${mrrChange >= 0 ? "+" : ""}${fmt(mrrChange)}`}
                icon={mrrChange >= 0 ? TrendingUp : TrendingDown}
                badge={mrrChange >= 0 ? { text: "▲", variant: "default" } : { text: "▼", variant: "destructive" }}
                onClick={() => handleKpiClick("mrr")}
              />
              <KpiCard
                label="Active Companies" value={String(totals.active)} icon={Building2}
                sub={`${totals.projects} projects`}
                onClick={() => handleKpiClick("active")}
                active={statusFilter === "active" && !churnOnly}
              />
              <KpiCard
                label="Trial Companies" value={String(totals.trial)} icon={Building2}
                onClick={() => handleKpiClick("trial")}
                active={statusFilter === "trial"}
              />
              <KpiCard label="Total Users" value={`${totals.users} of ${totals.seats}`} icon={Users} sub="seats used" />
              <KpiCard
                label="Churn Risk" value={String(churnRiskCount)} icon={AlertTriangle}
                badge={churnRiskCount > 0 ? { text: String(churnRiskCount), variant: "destructive" } : undefined}
                onClick={() => handleKpiClick("churn")}
                active={churnOnly}
              />
              <KpiCard label="ARR" value={fmt(totals.mrr * 12)} icon={BarChart3} onClick={() => handleKpiClick("arr")} />
              <KpiCard
                label="Avg Revenue / Account" value={fmt(avgRevenue)} icon={DollarSign}
                sub={`${payingCount} paying`}
                onClick={() => handleKpiClick("avg")}
              />
            </div>

            {/* MRR Sparkline */}
            {trendData.length > 1 && (
              <div className="mt-3 flex items-center gap-4">
                <div className="h-10 flex-1 max-w-xs">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="mrr" stroke="hsl(var(--primary))" strokeWidth={1.5} fill="url(#sparkFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <span className="text-sm font-semibold text-primary">{fmt(currentMrr)}</span>
              </div>
            )}
          </section>

          {/* Critical Incidents Widget */}
          {criticalIncidents.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  Open Incidents ({openIncidentCount})
                </h2>
                <Button variant="link" size="sm" className="text-xs h-auto p-0" onClick={() => navigate("/platform/incidents")}>
                  View all →
                </Button>
              </div>
              <div className="space-y-2">
                {criticalIncidents.slice(0, 3).map((incident) => (
                  <IncidentCard
                    key={incident.id}
                    incident={incident}
                    compact
                    onInvestigate={() => navigate("/platform/incidents")}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Search + Filters */}
          <section className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search companies…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_FILTERS.map((s) => (
                <Badge
                  key={s}
                  variant={statusFilter === s && !churnOnly ? "default" : "outline"}
                  className="cursor-pointer capitalize"
                  onClick={() => { setStatusFilter(s); setChurnOnly(false); }}
                >
                  {s === "all" ? "All" : s.replace("_", " ")}
                </Badge>
              ))}
              {churnOnly && (
                <Badge variant="destructive" className="cursor-pointer" onClick={() => setChurnOnly(false)}>
                  Churn Risk ✕
                </Badge>
              )}
            </div>
          </section>

          {/* Company Table */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground mb-3">
              Companies {filtered.length !== companies.length && `(${filtered.length} of ${companies.length})`}
            </h2>
            {filtered.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-muted-foreground">
                  {companies.length === 0
                    ? 'No companies found. Use "Onboard Company" to add your first tenant.'
                    : "No companies match the current filters."}
                </CardContent>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Projects</TableHead>
                      <TableHead className="text-right">Users</TableHead>
                      <TableHead className="text-right">MRR</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((c) => {
                      const isChurnRisk = c.project_count === 0 && c.subscription_status === "active";
                      return (
                        <TableRow
                          key={c.company_id}
                          className="cursor-pointer"
                          onClick={() => navigate(`/platform/${c.company_id}`)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
                              {c.company_name}
                              {isChurnRisk && <Badge variant="destructive" className="text-[10px] px-1">Risk</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${tierColor[c.plan_tier] ?? "bg-muted text-muted-foreground"} text-xs capitalize`}>
                              {c.plan_tier === "none" ? "—" : c.plan_tier}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${statusColor[c.subscription_status] ?? "bg-muted text-muted-foreground"} text-xs capitalize`}>
                              {c.subscription_status === "none" ? "—" : c.subscription_status.replace("_", " ")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{c.project_count}</TableCell>
                          <TableCell className="text-right">{c.active_users} / {c.seat_count}</TableCell>
                          <TableCell className="text-right font-medium">{fmt(c.calculated_mrr)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              {can("change_billing") && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" title="Manage subscription" onClick={() => setEditCompany(c)}>
                                  <Settings className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {can("impersonate") && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" title="Login as tenant" onClick={() => setImpersonateTarget(c)}>
                                  <UserCheck className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Open detail" onClick={() => navigate(`/platform/${c.company_id}`)}>
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            )}
          </section>
        </>
      )}

      {/* Dialogs */}
      <EditSubscriptionDialog company={editCompany} open={!!editCompany} onOpenChange={(o) => { if (!o) setEditCompany(null); }} />

      <Dialog open={!!impersonateTarget} onOpenChange={(o) => { if (!o) setImpersonateTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Admin Impersonation
            </DialogTitle>
            <DialogDescription>
              You are about to access <strong>{impersonateTarget?.company_name}</strong>'s workspace as an admin.
              This action will be logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonateTarget(null)}>Cancel</Button>
            <Button onClick={handleImpersonate}>Confirm &amp; Enter Workspace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAdminDashboard;
